import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Input, Modal, Select, Table, Tag, Tooltip } from 'antd';
import { EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/lib/table';
import BreadCrumbs from '../../../common/components/Breadcrumbs';
import { Action, Page } from '../../../common/constants/pageAction';
import ability from '../../../common/ability';
import snAiSettingsService, {
  ISnAiSetting,
  SettingSource,
} from '../../../services/cmdb/snAiSettings/snAiSettings.service';

const { Option } = Select;

const SOURCE_TAGS: Record<SettingSource, { color: string; label: string; help: string }> = {
  table: { color: 'blue', label: 'Set here', help: 'Saved on this page' },
  environment: {
    color: 'orange',
    label: 'Azure (legacy)',
    help: 'Old Azure App Service setting of the same name. Save the value here, then remove it from Azure.',
  },
  default: { color: 'default', label: 'Built-in default', help: 'Nothing saved here; the default in the code' },
};

// Settings with a fixed set of values get a dropdown; everything else is free text validated by the API.
const CHOICES: Record<string, string[]> = {
  AI_SN_SCHEDULER_ENABLED: ['true', 'false'],
  AI_SN_RULES_AUTO_ACTIVATE: ['true', 'false'],
  AI_SN_EFFORT: ['low', 'medium', 'high', 'xhigh', 'max'],
  AI_SN_RULE_EFFORT: ['low', 'medium', 'high', 'xhigh', 'max'],
  AI_SN_AUTO_ACCEPT_MIN_CONFIDENCE: ['High', 'Medium', 'Low'],
  AI_SN_OTHER_MIN_CONFIDENCE: ['High', 'Medium', 'Low'],
};

const BLANK = '__blank__';

const SnAiSettings: React.FC = () => {
  const [data, setData] = useState<ISnAiSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const [editTarget, setEditTarget] = useState<ISnAiSetting | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const canEdit = ability.can(Action.Update, Page.CmdbSnAiSettings);
  // Azure app settings are being retired; only show that column while any are still set.
  const anyAzure = data.some((d) => d.environmentValue !== undefined && d.environmentValue !== null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await snAiSettingsService.list());
    } catch { /* handled by request interceptor */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (setting: ISnAiSetting) => {
    setEditTarget(setting);
    form.setFieldsValue({ setting_value: setting.tableValue ?? (CHOICES[setting.key] ? BLANK : '') });
  };

  const handleSave = async () => {
    if (!editTarget) return;
    const values = await form.validateFields();
    const raw = values.setting_value === BLANK ? '' : (values.setting_value ?? '').trim();
    setSaving(true);
    try {
      await snAiSettingsService.update(editTarget.key, raw === '' ? null : raw);
      toast.success(`${editTarget.key} saved. It applies from the next run.`);
      setEditTarget(null);
      load();
    } catch { /* validation message shown by request interceptor */ } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<ISnAiSetting> = [
    {
      title: 'Setting',
      key: 'key',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.key}</div>
          <div style={{ color: '#666', fontSize: 12 }}>{record.description}</div>
        </div>
      ),
    },
    {
      title: 'Value in use',
      key: 'effectiveValue',
      width: 260,
      render: (_, record) => (
        <span>
          {record.effectiveValue ?? <i style={{ color: '#999' }}>not set (model or adaptive default)</i>}
          {record.warning && (
            <Tooltip title={record.warning}>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: 8 }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 150,
      render: (source: SettingSource) => (
        <Tooltip title={SOURCE_TAGS[source].help}>
          <Tag color={SOURCE_TAGS[source].color}>{SOURCE_TAGS[source].label}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Built-in default',
      dataIndex: 'defaultValue',
      key: 'defaultValue',
      width: 180,
      render: (v?: string) => <span style={{ color: '#666' }}>{v ?? '—'}</span>,
    },
    ...(anyAzure
      ? [
          {
            title: 'Azure (legacy)',
            dataIndex: 'environmentValue',
            key: 'environmentValue',
            width: 180,
            render: (v?: string) => <span style={{ color: '#999' }}>{v ?? '—'}</span>,
          },
        ]
      : []),
    {
      title: '',
      key: 'actions',
      width: 60,
      render: (_, record) =>
        canEdit && (
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
        ),
    },
  ];

  return (
    <div className="sqlServer">
      <div className="title-block">
        <h4 className="p-0">
          <BreadCrumbs pageName={Page.CmdbSnAiSettings} />
        </h4>
      </div>

      <div className="main-card">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Software Normalization AI settings"
          description={`Saving a value here takes effect from the next run, with no restart. Clear a value to use the built-in default. The schedule times and the API key are not on this page; they stay in Azure.${
            anyAzure ? ' Rows marked Azure (legacy) still come from an old Azure app setting: save the value here, then remove it from Azure.' : ''
          }`}
        />
        <Table<ISnAiSetting>
          rowKey="key"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
        />
      </div>

      <Modal
        title={editTarget ? `Edit ${editTarget.key}` : ''}
        visible={!!editTarget}
        onCancel={() => setEditTarget(null)}
        onOk={handleSave}
        okText="Save"
        okButtonProps={{ loading: saving }}
        centered
        destroyOnClose
        width={560}
      >
        {editTarget && (
          <Form form={form} layout="vertical">
            <p style={{ color: '#666' }}>{editTarget.description}</p>
            <Form.Item
              name="setting_value"
              label="Value"
              extra={`Blank uses the built-in default (${editTarget.defaultValue ?? 'not set'})${
                editTarget.environmentValue ? `, unless the legacy Azure setting (${editTarget.environmentValue}) is still present` : ''
              }.`}
            >
              {CHOICES[editTarget.key] ? (
                <Select>
                  <Option value={BLANK}>(blank: use built-in default)</Option>
                  {CHOICES[editTarget.key].map((c) => (
                    <Option key={c} value={c}>{c}</Option>
                  ))}
                </Select>
              ) : (
                <Input maxLength={1000} allowClear placeholder="Leave blank to use the built-in default" />
              )}
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default SnAiSettings;
